// src/pages/CompanyManagement/tabs/SupportTypesTab.jsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Button,
  IconButton,
  Divider,
  Alert,
  List,
  ListItem,
  Grid,
  Chip
} from '@mui/material';
import {
  ConfirmationNumber as TicketIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const SupportTypesTab = ({ company, onChange, t }) => {
  const settings = company.settings || {};

  // Default support types (matching the defaults in backend)
  const defaultSupportTypes = [
    { value: 'domotics', label: 'Domotics', enabled: true },
    { value: 'access_control', label: 'Access Control', enabled: true },
    { value: 'tablet', label: 'Tablet', enabled: true },
    { value: 'emergency_system', label: 'Emergency System', enabled: true },
    { value: 'it_pc', label: 'IT/PC', enabled: true },
    { value: 'other', label: 'Other', enabled: true }
  ];

  // Initialize with defaults if empty, otherwise use existing
  const supportTypes = settings.ticketSupportTypes?.length > 0
    ? settings.ticketSupportTypes
    : defaultSupportTypes;

  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newType, setNewType] = useState({ value: '', label: '' });
  const [addingNew, setAddingNew] = useState(false);

  const handleToggleEnabled = (index) => {
    const updated = [...supportTypes];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };

    onChange({
      settings: {
        ...settings,
        ticketSupportTypes: updated
      }
    });
  };

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditValue(supportTypes[index].label);
  };

  const handleSaveEdit = () => {
    const updated = [...supportTypes];
    updated[editingIndex] = { ...updated[editingIndex], label: editValue };

    onChange({
      settings: {
        ...settings,
        ticketSupportTypes: updated
      }
    });

    setEditingIndex(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleDelete = (index) => {
    if (!window.confirm(t('CompanyManagement.delete_support_type_confirm'))) return;

    const updated = supportTypes.filter((_, i) => i !== index);

    onChange({
      settings: {
        ...settings,
        ticketSupportTypes: updated
      }
    });
  };

  const handleAddNew = () => {
    if (!newType.value || !newType.label) return;

    // Check if value already exists
    if (supportTypes.some(t => t.value === newType.value)) {
      alert(t('CompanyManagement.support_type_exists'));
      return;
    }

    const updated = [...supportTypes, { ...newType, enabled: true }];

    onChange({
      settings: {
        ...settings,
        ticketSupportTypes: updated
      }
    });

    setNewType({ value: '', label: '' });
    setAddingNew(false);
  };

  const handleCancelAdd = () => {
    setNewType({ value: '', label: '' });
    setAddingNew(false);
  };

  const handleResetToDefaults = () => {
    if (!window.confirm(t('CompanyManagement.reset_support_types_confirm'))) {
      return;
    }

    onChange({
      settings: {
        ...settings,
        ticketSupportTypes: defaultSupportTypes
      }
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {t('CompanyManagement.support_types_title')}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleResetToDefaults}
        >
          {t('CompanyManagement.reset_to_defaults')}
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TicketIcon color="primary" />
            <Typography variant="h6">
              {t('CompanyManagement.manage_support_types')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 3 }}>
            {t('CompanyManagement.manage_support_types_info')}
          </Alert>

          {/* Support Types List */}
          <List>
            {supportTypes.map((type, index) => (
              <ListItem
                key={type.value}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: type.enabled ? 'background.paper' : 'action.hover'
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Chip
                      label={type.value}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    {editingIndex === index ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      <Typography variant="body1" fontWeight="500">
                        {type.label}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Switch
                      checked={type.enabled}
                      onChange={() => handleToggleEnabled(index)}
                      disabled={editingIndex === index}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {editingIndex === index ? (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSaveEdit}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                          >
                            <CloseIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleStartEdit(index)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </ListItem>
            ))}
          </List>

          {/* Add New Support Type */}
          {addingNew ? (
            <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('CompanyManagement.add_new_support_type')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('CompanyManagement.support_type_value_label')}
                    value={newType.value}
                    onChange={(e) => setNewType({ ...newType, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    helperText={t('CompanyManagement.support_type_value_helper')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('CompanyManagement.support_type_label_label')}
                    value={newType.label}
                    onChange={(e) => setNewType({ ...newType, label: e.target.value })}
                    helperText={t('CompanyManagement.support_type_label_helper')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={handleCancelAdd}
                    >
                      {t('CompanyManagement.cancel')}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleAddNew}
                      disabled={!newType.value || !newType.label}
                    >
                      {t('CompanyManagement.add')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          ) : (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAddingNew(true)}
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
            >
              {t('CompanyManagement.add_custom_support_type')}
            </Button>
          )}

          {/* Summary */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('CompanyManagement.support_types_summary', { enabled: supportTypes.filter(t => t.enabled).length, total: supportTypes.length })}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SupportTypesTab;
